import pluralize from "pluralize";
import { useCallback, useState } from "react";
import { useMutation } from "urql";

import { UpdateAsset } from "@/features/asset/queries.graphql";
import { SessionObjectFragment } from "@/features/forge/forge-session/queries.graphql";

import { MoveToDirectory } from "../../features/drive/queries.graphql";
import { useRequiredWorkspace } from "../useRequiredWorkspace";
import useShowSnackbar from "../useShowSnackbar";
import { useUrqlResponseHandler } from "../useUrqlResponseHandler";

export const useSaveToDrive = () => {
  const { workspace } = useRequiredWorkspace();
  const { showSuccess, showError } = useShowSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const [, updateAsset] = useMutation(UpdateAsset);
  const [, moveToDirectory] = useMutation(MoveToDirectory);
  const { getResponseFromState } = useUrqlResponseHandler();

  const saveFilesToDrive = async (
    fileIds: string[],
    directoryPath: string,
    workspaceId: string,
  ): Promise<void> => {
    const result = await moveToDirectory({
      moveToDirectoryInput: {
        workspaceId,
        targetDirectoryPath: directoryPath,
        fileIds,
        intent: "EXPORT_ASSET",
      },
    });

    const response = getResponseFromState({
      state: result,
      showSnackbarOnError: false,
    });

    if (response.error) {
      console.error("unable to save files to drive", response.error);
      throw new Error("Unable to save files to Drive");
    }
  };

  const saveAssetToDrive = async (
    assetId: string,
    directoryPath: string,
  ): Promise<void> => {
    const result = await updateAsset({
      updateAssetInput: {
        assetId,
        directory: directoryPath !== "Drive" ? directoryPath : "/",
      },
    });

    const response = getResponseFromState({
      state: result,
      showSnackbarOnError: false,
    });

    if (response.error) {
      console.error("unable to save asset to drive", response.error);
      throw new Error("Unable to save asset to Drive");
    }
  };

  const saveToDrive = useCallback(
    async (objects: SessionObjectFragment[], directoryPath: string) => {
      setIsLoading(true);

      const fileIds = objects
        .filter(
          (obj): obj is SessionObjectFragment & { __typename: "File" } =>
            obj.__typename === "File",
        )
        .map((file) => file.id);

      const assetIds = objects
        .filter(
          (obj): obj is SessionObjectFragment & { __typename: "Asset" } =>
            obj.__typename === "Asset",
        )
        .map((asset) => asset.id);

      const promises: Promise<void>[] = assetIds.map((assetId) =>
        saveAssetToDrive(assetId, directoryPath),
      );

      if (fileIds.length > 0) {
        const promise = saveFilesToDrive(fileIds, directoryPath, workspace.id);
        promises.push(promise);
      }

      try {
        await Promise.all(promises);
        showSuccess(
          `Saved ${objects.length} ${pluralize("item", objects.length)} to Drive.`,
        );
      } catch {
        showError(
          `We could not save your ${pluralize("item", objects.length)} to Drive.`,
        );
      }

      setIsLoading(false);
    },
    [moveToDirectory, getResponseFromState, workspace, showSuccess, showError],
  );

  return { saveToDrive, isLoading };
};
